import { useEffect, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { SelectorSimple } from '@xiltrion/ui/bloques/selectores';
import { EstadoCargando } from '@xiltrion/ui/bloques/estados';
import { Alert, AlertDescription } from '@xiltrion/ui/alert';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@xiltrion/ui/dialog';
import { Icono } from '@xiltrion/ui/icono';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@xiltrion/ui/input-group';
import { avisar } from '@xiltrion/ui/sonner';
import { Switch } from '@xiltrion/ui/switch';
import { apiGet, apiPost, apiPut, errorMessage } from '@/api/client';
import type { AppRoleCatalogRow, EntityAppRow, MemberRow, RoleGrantRow } from '@/api/types';

type Draft = { access: boolean; roles: RoleGrantRow[]; touched: boolean };

/** Nombre legible de un rol asignado («Funcionario · Salud»). */
export function grantLabel(g: RoleGrantRow, catalog?: AppRoleCatalogRow | null) {
  const role = catalog?.roles.find((r) => r.code === g.role)?.name ?? g.role;
  const scope = g.scope ? catalog?.scopes?.options.find((o) => o.value === g.scope)?.label ?? g.scope : null;
  return scope ? `${role} · ${scope}` : role;
}

/**
 * Acceso y roles de una persona en cada app de la entidad (GUIA §3.9): los roles vienen del catálogo que cada app
 * publica (con los propios de la entidad), pedido de nuevo a la app al abrir, y, si el rol se limita, se elige su alcance.
 */
export function MemberAppsDialog({ slug, member, apps, generateLink, onClose }: { slug: string; member: MemberRow; apps: EntityAppRow[]; generateLink?: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const catalogs = useQueries({ queries: apps.map((a) => ({ queryKey: ['app-roles', slug, a.code], queryFn: () => apiGet<AppRoleCatalogRow | null>(`/entities/${slug}/apps/${a.code}/roles?refresh=1`), retry: false, staleTime: 0, refetchOnMount: 'always' as const })) });
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [saving, setSaving] = useState(false);
  const isAdmin = member.role === 'ADMIN';
  useEffect(() => {
    setDrafts(Object.fromEntries(apps.map((a) => [a.code, { access: isAdmin || member.apps.includes(a.code), roles: member.appRoles[a.code] ?? [], touched: false }])));
  }, [apps, member, isAdmin]);
  const set = (code: string, patch: Partial<Draft>) => setDrafts((d) => ({ ...d, [code]: { ...d[code]!, ...patch, touched: true } }));
  const save = async () => {
    setSaving(true);
    try {
      for (const [i, a] of apps.entries()) {
        const d = drafts[a.code];
        if (!d?.touched) continue;
        const scoped = (code: string) => !!catalogs[i]?.data?.roles.find((r) => r.code === code)?.scoped;
        await apiPut(`/entities/${slug}/members/${member.id}/apps/${a.code}`, { access: d.access, roles: d.roles.map((g) => ({ role: g.role, scope: scoped(g.role) ? g.scope || null : null })) });
      }
      avisar.success('Acceso y roles guardados; llegan solos a cada app');
      await qc.invalidateQueries({ queryKey: ['entity', slug] });
      onClose();
    } catch (e) {
      avisar.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle>{member.displayName}</DialogTitle><DialogDescription>A qué apps entra y con qué roles. {isAdmin ? 'Como administradora de la entidad entra a todas las apps.' : ''}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {(member.userStatus === 'INVITED' || member.authProvider === 'local') && <ShareLink slug={slug} member={member} auto={generateLink} />}
          {apps.map((a, i) => {
            const q = catalogs[i]!;
            const catalog = q.data ?? null;
            const d = drafts[a.code];
            if (!d) return null;
            const managed = a.code in member.appRoles;
            const roleOptions = (catalog?.roles ?? []).filter((r) => r.assignable).map((r) => ({ value: r.code, label: r.name, descripcion: r.description ?? undefined, extra: r.custom ? <Badge size="sm" variant="secondary">propio</Badge> : undefined }));
            const anyScoped = !!catalog?.scopes && catalog.roles.some((r) => r.scoped);
            return (
              <div key={a.code} className="space-y-3 rounded-lg border p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2"><Icono nombre={a.icon as never} className="size-5 text-primary" /><span className="font-medium">{a.name}</span></div>
                  <Switch checked={d.access} disabled={isAdmin} onCheckedChange={(v) => set(a.code, { access: v, roles: v && !d.roles.length && catalog ? (catalog.roles.filter((r) => (isAdmin ? r.admin : r.default)).slice(0, 1).map((r) => ({ role: r.code, scope: null }))) : d.roles })} aria-label={`Acceso a ${a.name}`} />
                </div>
                {d.access && (q.isLoading ? <EstadoCargando /> : q.isError ? (
                  <Alert variant="warning"><Icono nombre="mdi:lan-disconnect" /><AlertDescription>No se pudieron consultar los roles de {a.name}: la app no respondió. Revisa que esté en línea y conectada a la suite; mientras tanto, sus roles se manejan en la app.</AlertDescription></Alert>
                ) : !catalog ? (
                  <Alert variant="info"><Icono nombre="mdi:information-outline" /><AlertDescription>{a.name} todavía no publica sus roles: los maneja la app.</AlertDescription></Alert>
                ) : (
                  <div className="space-y-2">
                    {!managed && !d.touched && <Alert variant="info"><AlertDescription>{a.name} conserva los roles que esta persona ya tenía. Al guardar aquí, la suite pasa a administrarlos.</AlertDescription></Alert>}
                    {d.roles.map((g, gi) => {
                      const role = catalog.roles.find((r) => r.code === g.role);
                      return (
                        <div key={gi} className={`grid items-center gap-2 ${anyScoped ? 'grid-cols-[1fr_1fr_auto]' : 'grid-cols-[1fr_auto]'}`}>
                          <SelectorSimple value={g.role} options={roleOptions} onChange={(v) => set(a.code, { roles: d.roles.map((x, xi) => (xi === gi ? { role: v ?? x.role, scope: null } : x)) })} aria-label="Rol" />
                          {anyScoped && (role?.scoped && catalog.scopes ? <SelectorSimple value={g.scope ?? null} options={catalog.scopes.options.map((o) => ({ value: o.value, label: o.label }))} limpiable={role.scopeOptional} textoSinValor="Toda la entidad" placeholder={catalog.scopes.label} onChange={(v) => set(a.code, { roles: d.roles.map((x, xi) => (xi === gi ? { ...x, scope: v } : x)) })} aria-label={catalog.scopes.label} /> : <span />)}
                          <Button variant="ghost" size="icon" aria-label="Quitar rol" onClick={() => set(a.code, { roles: d.roles.filter((_, xi) => xi !== gi) })}><Icono nombre="mdi:close" /></Button>
                        </div>
                      );
                    })}
                    <Button variant="outline" size="sm" disabled={!roleOptions.length} onClick={() => set(a.code, { roles: [...d.roles, { role: roleOptions[0]!.value, scope: null }] })}><Icono nombre="mdi:plus" />Agregar rol</Button>
                    {!d.roles.length && (managed || d.touched) && <p className="text-xs text-muted-foreground">Sin roles, entra a {a.name} pero no ve nada.</p>}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancelar</Button><Button disabled={saving || !Object.values(drafts).some((d) => d.touched)} onClick={() => void save()}>{saving ? 'Guardando…' : 'Guardar'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Enlace para compartir a mano (la entidad no tiene correo o no llegó): activar la cuenta de quien está invitado, o
 * elegir contraseña nueva para quien ya la activó con contraseña de la suite. Del enlace solo se guarda su hash: se emite
 * uno nuevo y el anterior deja de servir.
 */
function ShareLink({ slug, member, auto }: { slug: string; member: MemberRow; auto?: boolean }) {
  const invited = member.userStatus === 'INVITED';
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    try {
      setUrl((await apiPost<{ url: string }>(`/entities/${slug}/members/${member.id}/${invited ? 'activation-link' : 'password-reset-link'}`)).url);
    } catch (e) {
      avisar.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (auto) void generate();
    // Solo al abrir: generar de nuevo invalidaría el enlace recién mostrado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const copy = () => void navigator.clipboard.writeText(url!).then(() => avisar.success('Enlace copiado'), () => avisar.error('No se pudo copiar el enlace'));
  return (
    <Alert variant={invited ? 'warning' : 'info'}>
      <Icono nombre={invited ? 'mdi:account-clock-outline' : 'mdi:lock-reset'} />
      <AlertDescription className="space-y-3">
        <p>{invited
          ? `${member.displayName} aún no activa su cuenta. Comparte este enlace para que elija su contraseña; el que se haya enviado antes deja de servir.`
          : `Si ${member.displayName} olvidó su contraseña y no le llega el correo, comparte este enlace para que elija una nueva; el anterior deja de servir.`}</p>
        {url ? (
          <InputGroup>
            <InputGroupInput readOnly value={url} aria-label={invited ? 'Enlace de activación' : 'Enlace para restablecer la contraseña'} onFocus={(e) => e.currentTarget.select()} />
            <InputGroupAddon align="inline-end"><InputGroupButton size="sm" onClick={copy}><Icono nombre="mdi:content-copy" />Copiar</InputGroupButton></InputGroupAddon>
          </InputGroup>
        ) : (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void generate()}><Icono nombre="mdi:link-variant" />{busy ? 'Generando…' : invited ? 'Generar enlace de activación' : 'Generar enlace para restablecer la contraseña'}</Button>
        )}
      </AlertDescription>
    </Alert>
  );
}
